<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { NodeResizer } from '@vue-flow/node-resizer'
import { Handle, Position, useNode } from '@vue-flow/core'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent
} from '@/components/ui/dropdown-menu'
import { MoreHorizontal, Pencil, Trash2, Check, X, Image, ImageOff, ChevronDown, ChevronRight } from 'lucide-vue-next'
import { useGraphContainersStore } from '@/stores/graphContainers'
import { useImageLibraryStore } from '@/stores/imageLibrary'
import { useImageCacheStore } from '@/stores/imageCache'
import '@vue-flow/node-resizer/dist/style.css'

export interface ContainerData {
  id: string
  name: string
  color: string
  background_image_path: string | null
  width: number
  height: number
  is_collapsed: boolean
}

interface Props {
  data: ContainerData
  isMobile?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  rename: [id: string, name: string]
  resize: [id: string, width: number, height: number]
  changeColor: [id: string, color: string]
  changeBackgroundImage: [id: string, imagePath: string | null]
  delete: [id: string]
  toggleCollapse: [id: string]
}>()

const { node } = useNode()
const graphContainersStore = useGraphContainersStore()
const imageLibraryStore = useImageLibraryStore()
const imageCacheStore = useImageCacheStore()

// Background image URL
const backgroundImageUrl = ref<string | null>(null)

// Image picker modal state
const showImagePicker = ref(false)

// Load background image URL using the image cache
const loadBackgroundImage = async () => {
  if (props.data.background_image_path) {
    backgroundImageUrl.value = await imageCacheStore.getImageByPath(props.data.background_image_path, 'user-images')
  } else {
    backgroundImageUrl.value = null
  }
}

// Watch for background image path changes
watch(() => props.data.background_image_path, loadBackgroundImage, { immediate: true })

// Load image library
onMounted(async () => {
  await imageLibraryStore.fetchImages()
})

// Editing state
const isEditing = ref(false)
const editName = ref(props.data.name)

// Available colors for container background
const DEFAULT_BORDER = 'hsl(var(--border))'
const colors = [
  { name: 'None', value: 'transparent', border: DEFAULT_BORDER },
  { name: 'Blue', value: 'rgba(59, 130, 246, 0.15)', border: '#3b82f6' },
  { name: 'Purple', value: 'rgba(139, 92, 246, 0.15)', border: '#8b5cf6' },
  { name: 'Green', value: 'rgba(34, 197, 94, 0.15)', border: '#22c55e' },
  { name: 'Orange', value: 'rgba(249, 115, 22, 0.15)', border: '#f97316' },
  { name: 'Pink', value: 'rgba(236, 72, 153, 0.15)', border: '#ec4899' },
  { name: 'Gold', value: 'var(--gold-bg)', border: 'var(--gold-solid)' },
]

const currentColor = computed(() => {
  return colors.find(c => c.value === props.data.color) || colors[0] // Default to none/transparent
})

const startEditing = () => {
  editName.value = props.data.name
  isEditing.value = true
}

const saveEdit = () => {
  if (editName.value.trim()) {
    emit('rename', props.data.id, editName.value.trim())
  }
  isEditing.value = false
}

const cancelEdit = () => {
  editName.value = props.data.name
  isEditing.value = false
}

const handleResize = (event: any) => {
  // NodeResizeControl passes { event, params } where params contains the new dimensions
  const params = event.params || event
  if (params && params.width && params.height) {
    emit('resize', props.data.id, params.width, params.height)
  }
}

const changeColor = (color: string) => {
  emit('changeColor', props.data.id, color)
}

const openImagePicker = () => {
  showImagePicker.value = true
}

const selectBackgroundImage = (imagePath: string) => {
  emit('changeBackgroundImage', props.data.id, imagePath)
  showImagePicker.value = false
}

const removeBackgroundImage = () => {
  emit('changeBackgroundImage', props.data.id, null)
}

// Compute border color from background color
const borderColor = computed(() => {
  const color = colors.find(c => c.value === props.data.color)
  return color?.border || DEFAULT_BORDER
})

// Collapsed state
const COLLAPSED_HEIGHT = 50
const COLLAPSED_WIDTH = 200

const displayHeight = computed(() => {
  return props.data.is_collapsed ? COLLAPSED_HEIGHT : props.data.height
})

const displayWidth = computed(() => {
  return props.data.is_collapsed ? COLLAPSED_WIDTH : props.data.width
})

// Animated SVG dimensions for smooth collapse/expand
const animatedSvgWidth = ref(props.data.width)
const animatedSvgHeight = ref(props.data.height)
let animationFrameId: number | null = null

// Cubic bezier function matching CSS cubic-bezier(0.4, 0, 0.2, 1)
const cubicBezier = (t: number): number => {
  // cubic-bezier(0.4, 0, 0.2, 1) - Material Design standard easing
  const p1x = 0.4, p1y = 0, p2x = 0.2, p2y = 1
  
  // Binary search for t value
  let low = 0, high = 1
  for (let i = 0; i < 8; i++) {
    const mid = (low + high) / 2
    const x = 3 * (1 - mid) * (1 - mid) * mid * p1x + 3 * (1 - mid) * mid * mid * p2x + mid * mid * mid
    if (x < t) low = mid
    else high = mid
  }
  const finalT = (low + high) / 2
  return 3 * (1 - finalT) * (1 - finalT) * finalT * p1y + 3 * (1 - finalT) * finalT * finalT * p2y + finalT * finalT * finalT
}

const animateSvgDimensions = (targetWidth: number, targetHeight: number, duration = 250) => {
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
  }
  
  const startWidth = animatedSvgWidth.value
  const startHeight = animatedSvgHeight.value
  const startTime = performance.now()
  
  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    
    const eased = cubicBezier(progress)
    
    animatedSvgWidth.value = startWidth + (targetWidth - startWidth) * eased
    animatedSvgHeight.value = startHeight + (targetHeight - startHeight) * eased
    
    if (progress < 1) {
      animationFrameId = requestAnimationFrame(animate)
    } else {
      animationFrameId = null
    }
  }
  
  animationFrameId = requestAnimationFrame(animate)
}

// Watch for dimension changes and animate
watch([displayWidth, displayHeight], ([newWidth, newHeight]) => {
  animateSvgDimensions(newWidth, newHeight)
}, { immediate: true })

onUnmounted(() => {
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
  }
})

const toggleCollapse = () => {
  emit('toggleCollapse', props.data.id)
}

// SVG path for decorative frame
const uniqueId = computed(() => `container-${props.data.id}-${Math.random().toString(36).slice(2, 9)}`)
</script>

<template>
  <div 
    class="container-node"
    :class="{ 'is-collapsed': data.is_collapsed }"
    :style="{
      width: `${displayWidth}px`,
      height: `${displayHeight}px`,
      '--container-bg': data.color || 'transparent',
      '--container-border': borderColor
    }"
  >
    <!-- Background Image (shown on top of color) -->
    <div 
      v-if="backgroundImageUrl && !data.is_collapsed" 
      class="container-background-image"
      :style="{ backgroundImage: `url(${backgroundImageUrl})` }"
    />
    <!-- Node Resizer - only show when not collapsed -->
    <NodeResizer 
      v-if="!data.is_collapsed"
      :min-width="200" 
      :min-height="150"
      @resize="handleResize"
    />
    
    <!-- Decorative SVG Frame -->
    <svg 
      class="container-frame-svg"
      :viewBox="`0 0 ${animatedSvgWidth} ${animatedSvgHeight}`"
      preserveAspectRatio="none"
    >
      <defs>
        <!-- Gradient for frame -->
        <linearGradient :id="`${uniqueId}-gradient`" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" :stop-color="borderColor" stop-opacity="0.8" />
          <stop offset="50%" :stop-color="borderColor" stop-opacity="1" />
          <stop offset="100%" :stop-color="borderColor" stop-opacity="0.8" />
        </linearGradient>
        
        <!-- Glow filter -->
        <filter :id="`${uniqueId}-glow`" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        
        <!-- Clip path for chamfered corners -->
        <clipPath :id="`${uniqueId}-clip`">
          <path
            :d="`
              M 16 2
              L ${animatedSvgWidth - 16} 2
              L ${animatedSvgWidth - 2} 16
              L ${animatedSvgWidth - 2} ${animatedSvgHeight - 16}
              L ${animatedSvgWidth - 16} ${animatedSvgHeight - 2}
              L 16 ${animatedSvgHeight - 2}
              L 2 ${animatedSvgHeight - 16}
              L 2 16
              Z
            `"
          />
        </clipPath>
      </defs>
      
      <!-- Background with chamfered clip -->
      <rect 
        x="0" 
        y="0" 
        :width="animatedSvgWidth" 
        :height="animatedSvgHeight"
        :fill="data.color"
        :clip-path="`url(#${uniqueId}-clip)`"
      />
      
      <!-- Main border with chamfered corners -->
      <path
        :d="`
          M 16 2
          L ${animatedSvgWidth - 16} 2
          L ${animatedSvgWidth - 2} 16
          L ${animatedSvgWidth - 2} ${animatedSvgHeight - 16}
          L ${animatedSvgWidth - 16} ${animatedSvgHeight - 2}
          L 16 ${animatedSvgHeight - 2}
          L 2 ${animatedSvgHeight - 16}
          L 2 16
          Z
        `"
        fill="none"
        :stroke="`url(#${uniqueId}-gradient)`"
        stroke-width="2"
        stroke-linecap="square"
      />
      
      <!-- Corner accents - only when colored (not transparent) -->
      <template v-if="data.color !== 'transparent'">
      <!-- Corner accents - top left -->
      <path
        :d="`M 2 30 L 2 16 L 16 2 L 30 2`"
        fill="none"
        :stroke="borderColor"
        stroke-width="3"
        stroke-linecap="square"
        opacity="0.9"
      />
      
      <!-- Corner accents - top right -->
      <path
        :d="`M ${animatedSvgWidth - 30} 2 L ${animatedSvgWidth - 16} 2 L ${animatedSvgWidth - 2} 16 L ${animatedSvgWidth - 2} 30`"
        fill="none"
        :stroke="borderColor"
        stroke-width="3"
        stroke-linecap="square"
        opacity="0.9"
      />
      
      <!-- Corner accents - bottom left (only when not collapsed) -->
      <path
        v-if="!data.is_collapsed"
        :d="`M 2 ${animatedSvgHeight - 30} L 2 ${animatedSvgHeight - 16} L 16 ${animatedSvgHeight - 2} L 30 ${animatedSvgHeight - 2}`"
        fill="none"
        :stroke="borderColor"
        stroke-width="3"
        stroke-linecap="square"
        opacity="0.9"
      />
      
      <!-- Corner accents - bottom right (only when not collapsed) -->
      <path
        v-if="!data.is_collapsed"
        :d="`M ${animatedSvgWidth - 30} ${animatedSvgHeight - 2} L ${animatedSvgWidth - 16} ${animatedSvgHeight - 2} L ${animatedSvgWidth - 2} ${animatedSvgHeight - 16} L ${animatedSvgWidth - 2} ${animatedSvgHeight - 30}`"
        fill="none"
        :stroke="borderColor"
        stroke-width="3"
        stroke-linecap="square"
        opacity="0.9"
      />
      </template>
    </svg>
    
    <!-- Header -->
    <div class="container-header">
      <!-- Collapse Toggle Button -->
      <Button 
        variant="ghost" 
        size="icon" 
        class="h-6 w-6 collapse-btn" 
        @click="toggleCollapse"
        :title="data.is_collapsed ? 'Expand' : 'Collapse'"
      >
        <ChevronRight v-if="data.is_collapsed" class="h-4 w-4" />
        <ChevronDown v-else class="h-4 w-4" />
      </Button>
      
      <div class="container-title" v-if="!isEditing">
        <span class="container-name">{{ data.name }}</span>
      </div>
      <div class="container-edit" v-else>
        <Input 
          v-model="editName"
          class="h-6 text-xs w-32"
          @keyup.enter="saveEdit"
          @keyup.escape="cancelEdit"
          autofocus
        />
        <Button variant="ghost" size="icon" class="h-5 w-5" @click="saveEdit">
          <Check class="h-3 w-3" />
        </Button>
        <Button variant="ghost" size="icon" class="h-5 w-5" @click="cancelEdit">
          <X class="h-3 w-3" />
        </Button>
      </div>
      
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button variant="ghost" size="icon" class="h-6 w-6 container-menu-btn">
            <MoreHorizontal class="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" class="w-48">
          <DropdownMenuItem @click="startEditing">
            <Pencil class="h-4 w-4 mr-2" />
            Rename
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          <!-- Background Image Selection -->
          <DropdownMenuItem @click="openImagePicker">
            <Image class="h-4 w-4 mr-2" />
            Background Image
          </DropdownMenuItem>
          
          <DropdownMenuItem 
            v-if="data.background_image_path"
            @click="removeBackgroundImage"
          >
            <ImageOff class="h-4 w-4 mr-2" />
            Remove Background
          </DropdownMenuItem>
          
          <!-- Color Selection -->
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <div 
                class="h-4 w-4 mr-2 rounded-sm border"
                :style="{ backgroundColor: data.color, borderColor: borderColor }"
              />
              Background Color
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem 
                v-for="color in colors" 
                :key="color.value"
                @click="changeColor(color.value)"
              >
                <div 
                  class="h-4 w-4 mr-2 rounded-sm border"
                  :style="{ backgroundColor: color.value, borderColor: color.border }"
                />
                {{ color.name }}
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem class="text-destructive" @click="emit('delete', data.id)">
            <Trash2 class="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
    
    <!-- Content area for nested nodes (hidden when collapsed) -->
    <div v-if="!data.is_collapsed" class="container-content">
      <slot />
    </div>
    
    <!-- Image Picker Modal -->
    <Dialog v-model:open="showImagePicker">
      <DialogContent class="max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Select Background Image</DialogTitle>
          <DialogDescription>
            Choose an image from your library to set as the container background.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea class="h-[400px] pr-4">
          <div class="grid grid-cols-3 gap-3 p-1">
            <button
              v-for="image in imageLibraryStore.images"
              :key="image.id"
              class="relative aspect-video rounded-lg overflow-hidden border-2 border-transparent hover:border-primary transition-colors cursor-pointer group"
              @click="selectBackgroundImage(image.storage_path)"
            >
              <img
                :src="image.preview_url || image.url || ''"
                :alt="image.original_name"
                class="w-full h-full object-cover"
              />
              <div class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span class="text-white text-xs font-medium truncate px-2">{{ image.original_name }}</span>
              </div>
            </button>
          </div>
          <div v-if="imageLibraryStore.images.length === 0" class="flex flex-col items-center justify-center h-40 text-muted-foreground">
            <Image class="h-10 w-10 mb-2 opacity-50" />
            <p class="text-sm">No images in your library</p>
            <p class="text-xs">Upload images in the Image Library section</p>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  </div>
</template>

<style scoped>
.container-node {
  position: relative;
  background: transparent;
  border-radius: 8px;
  min-width: 200px;
  min-height: 150px;
  overflow: hidden;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.container-node.is-collapsed {
  min-width: 200px;
  min-height: 50px;
}

.container-background-image {
  position: absolute;
  inset: 2px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0.4;
  pointer-events: none;
  /* Chamfered corners clip - matches SVG frame shape */
  clip-path: polygon(
    16px 0,
    calc(100% - 16px) 0,
    100% 16px,
    100% calc(100% - 16px),
    calc(100% - 16px) 100%,
    16px 100%,
    0 calc(100% - 16px),
    0 16px
  );
}

.container-frame-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
}

/* Smooth transition for color changes */
.container-frame-svg path,
.container-frame-svg rect,
.container-frame-svg line {
  transition: stroke 400ms ease, fill 400ms ease;
}

.container-header {
  position: absolute;
  top: 8px;
  left: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 10;
}

.collapse-btn {
  flex-shrink: 0;
  color: var(--container-border);
  opacity: 0.7;
  transition: opacity 0.15s ease;
}

.collapse-btn:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.1);
}

.container-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.container-name {
  font-size: 12px;
  font-weight: 600;
  color: var(--container-border);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.container-edit {
  display: flex;
  align-items: center;
  gap: 4px;
}

.container-menu-btn {
  opacity: 0.6;
  transition: opacity 150ms;
}

.container-node:hover .container-menu-btn {
  opacity: 1;
}

.container-content {
  position: absolute;
  inset: 40px 16px 16px 16px;
  pointer-events: none;
}

/* Style the vue-flow node resizer */
:deep(.vue-flow__resize-control) {
  background: transparent !important;
  border: none !important;
}

:deep(.vue-flow__resize-control.handle) {
  width: 12px !important;
  height: 12px !important;
  border-radius: 2px !important;
  background: var(--container-border, var(--gold-solid)) !important;
  opacity: 0;
  transition: opacity 150ms;
}

.container-node:hover :deep(.vue-flow__resize-control.handle) {
  opacity: 0.6;
}

:deep(.vue-flow__resize-control.handle:hover) {
  opacity: 1 !important;
}

:deep(.vue-flow__resize-control.line) {
  border-color: var(--container-border, var(--gold-solid)) !important;
  opacity: 0;
  transition: opacity 150ms;
}

.container-node:hover :deep(.vue-flow__resize-control.line) {
  opacity: 0.3;
}

:deep(.vue-flow__resize-control.line:hover) {
  opacity: 0.6 !important;
}
</style>
